import type { MarketingPkDetailResp, MarketingPkMemberDto } from '@/api/modules/marketingPk'
import type { PkActivity, PkBroadcastStage, PkMember, PkMetric, PkParticipantConfig, PkRewardConfig } from './types'

/** 六指标展示元数据；不执行业务统计。 */
export const pkMetrics: Record<PkMetric, { name: string; unit: string; precision: number }> = {
  net_sales: { name: '净销售额', unit: '元', precision: 2 },
  valid_orders: { name: '有效成交单数', unit: '单', precision: 0 },
  first_orders: { name: '首购订单数', unit: '单', precision: 0 },
  repeat_orders: { name: '复购订单数', unit: '单', precision: 0 },
  product_sales_qty: { name: '销售商品数量', unit: '件', precision: 0 },
  fan_conversion_rate: { name: '加粉成交率', unit: '%', precision: 2 },
}

/** 格式化指标；metric 为服务端指标，masked/valid 分别表示隐藏和有效样本。 */
export function formatPkValue(
  value: number | undefined,
  metric: PkMetric,
  masked = false,
  valid = true,
): string {
  if (masked) return '暂不公开'
  if (!valid) return '无有效样本'
  if (value === undefined || !Number.isFinite(value)) return '—'
  const meta = pkMetrics[metric]
  if (!meta) return '不支持的指标'
  return `${value.toLocaleString('zh-CN', { minimumFractionDigits: meta.precision, maximumFractionDigits: meta.precision })} ${meta.unit}`
}

/** 解析服务器保存的配置；raw 为 JSON，不以假业务数据补齐损坏配置。 */
export function parsePkJson<T>(raw: string | undefined, fallback: T): T {
  if (!raw) return fallback
  return JSON.parse(raw) as T
}

/** 将成员 DTO 映射为页面行；rows 已由服务端按当前身份裁剪，metric 仅用于单位。 */
export function mapPkMembers(
  rows: MarketingPkMemberDto[],
  metric: PkMetric,
  participants: PkParticipantConfig[] = [],
): PkMember[] {
  const participantMap = new Map<number, PkParticipantConfig>()
  for (const p of participants) {
    if (p.employeeId) participantMap.set(p.employeeId, p)
  }
  // 有任何隐藏成员时不计算跨阵营排名，避免泄露相对成绩。
  const canRank = rows.every((row) => !row.isMasked && row.resultValid)
  const sorted = canRank ? [...rows].sort((a, b) => b.currentValue - a.currentValue) : rows
  return sorted.map((row, index) => {
    const config = participantMap.get(row.employeeId)
    return {
      ...row,
      id: String(row.employeeId),
      teamId: row.team,
      colorType: row.team,
      value: row.currentValue,
      unit: pkMetrics[metric]?.unit ?? '',
      rank: canRank ? index + 1 : 0,
      departmentId: config?.departmentId,
      departmentName: config?.departmentName || (config?.teamName !== row.name ? config?.teamName : undefined),
    }
  })
}

/** 详情和唯一微信侧边栏共用映射；response 是后端契约，rows 为同活动的真实成员。 */
export function mapPkDetail(
  response: MarketingPkDetailResp,
  rows: MarketingPkMemberDto[] = [],
): PkActivity {
  const a = response.activity
  const unit = pkMetrics[a.metricKey]?.unit ?? ''
  const participants = parsePkJson<PkParticipantConfig[]>(a.participantsJson, [])
  const members = mapPkMembers(rows, a.metricKey, participants)
  const stages = parsePkJson<PkBroadcastStage[]>(a.stagesJson, [])
  const snapshots = parsePkJson<
    Array<{
      stageIndex: number
      baseline: boolean
      triggerTeam: string
      achievedAt: string
      teamRedSnapshotValue?: number
      teamBlueSnapshotValue?: number
    }>
  >(a.snapshotsJson, [])
  const labels: Record<PkActivity['status'], string> = {
    draft: '草稿',
    pending_approval: '待审批',
    pending_start: '待开始',
    ongoing: '进行中',
    calm_period: '冷静期',
    pending_settlement: '待结算',
    finished: '已完结',
    canceled: '已取消',
  }
  const now = Date.parse(response.serverNow)
  const start = Date.parse(a.startTime)
  const end = Date.parse(a.endTime)
  return {
    ...a,
    id: String(a.id),
    code: a.activityNo,
    name: a.title,
    projectId: a.projectId,
    projectName: a.projectName || (a.projectId > 0 ? '项目已不可用' : '全部项目'),
    metric: a.metricKey,
    statusText: labels[a.status],
    prepareStartTime: '',
    applicantName: a.submitterName || a.creatorName,
    submitTime: a.submittedAt || a.createdAt,
    coolingEndTime: response.coolingDeadline,
    coolingRemainingSeconds: response.coolingDeadline
      ? Math.max(0, (Date.parse(response.coolingDeadline) - now) / 1000)
      : undefined,
    progressPercent:
      a.status === 'ongoing' && end > start
        ? Number(Math.min(100, Math.max(0, ((now - start) * 100) / (end - start))).toFixed(2))
        : 0,
    isOpponentHidden: response.sprintMaskActive,
    canManage: response.canManage,
    canSettle: response.canSettle,
    resultValid: response.resultValid,
    invalidReason: response.invalidReason,
    statisticsStatus: response.statisticsStatus,
    isDraw: a.winnerTeam === 'draw',
    winnerSummary:
      a.winnerTeam === 'invalid'
        ? '无有效结果，不生成奖惩'
        : a.winnerTeam === 'draw'
          ? '双方平局'
          : a.winnerTeam === 'red'
            ? `${response.redTeamStats.teamName || '红方'}获胜`
            : a.winnerTeam === 'blue'
              ? `${response.blueTeamStats.teamName || '蓝方'}获胜`
              : undefined,
    rewardConfig: parsePkJson<PkRewardConfig | undefined>(a.rewardsJson, undefined),
    teams: [response.redTeamStats, response.blueTeamStats].map((team, index) => {
      const side = index === 0 ? 'red' : 'blue'
      const sideParticipants = participants.filter((p) => p.team === side)
      const departmentName = sideParticipants[0]?.departmentName || (a.mode === 'department_1v1' ? sideParticipants[0]?.teamName : undefined)
      return {
        ...team,
        id: side,
        name: team.teamName || (index === 0 ? '红方' : '蓝方'),
        departmentName,
        colorType: side,
        unit,
        members: members.filter((m) => m.teamId === side),
      }
    }),
    stages: stages.map((stage) => {
      const matches = snapshots.filter((snap) => snap.stageIndex === stage.stage)
      const baseline = matches.some((snap) => snap.baseline)
      const first = matches.find((snap) => !snap.baseline)
      return {
        id: stage.id,
        name: stage.name,
        stageNumber: stage.stage,
        threshold: stage.targetAmount ?? 0,
        enabled: stage.enabled !== false,
        unit,
        baseline,
        records: matches.map((snap) => ({
          baseline: snap.baseline,
          teamId: snap.triggerTeam,
          teamName:
            snap.triggerTeam === 'red'
              ? response.redTeamStats.teamName || '红方'
              : response.blueTeamStats.teamName || '蓝方',
          recordedAt: snap.achievedAt,
          redValue: snap.teamRedSnapshotValue,
          blueValue: snap.teamBlueSnapshotValue,
        })),
        firstAchieverTeamId: first?.triggerTeam,
        firstAchieverTeamName: first
          ? first.triggerTeam === 'red'
            ? response.redTeamStats.teamName
            : response.blueTeamStats.teamName
          : undefined,
        firstAchievedAt: first?.achievedAt,
        teamRedSnapshotValue: first?.teamRedSnapshotValue,
        teamBlueSnapshotValue: first?.teamBlueSnapshotValue,
      }
    }),
  }
}

/** 按指标精度标准化门槛；value为表单值，非法或空值保持null，避免伪造门槛。 */
export function normalizePkThreshold(value: unknown, metric: PkMetric): number | null {
  if (value === null || value === undefined || value === '') return null
  const parsed = Number(value)
  if (!Number.isFinite(parsed) || parsed <= 0) return null
  return Number(parsed.toFixed(pkMetrics[metric].precision))
}

/** 默认 PK 庆祝与喜报背景图地址。 */
export const pkCelebrationDefaultImageUrl =
  'https://x-img.yaakoo123.cn/biz/public-assets/license/2026/09/b9eb24db-501a-48c7-84fe-1b52f0ba9eae.png'
